import { useState, useMemo } from 'react';
import type { SubBatch } from '../../hooks/usePipelineSnapshot';
import { SubBatchBlock } from './SubBatchBlock';

interface Props {
  batch: SubBatch[];
  realtime: SubBatch[];
  prevBatch?: SubBatch[];
  prevRealtime?: SubBatch[];
}

const PHASES = [
  { key: 'enrichment', label: 'Enrichment' },
  { key: 'benefit_scoring', label: 'Scoring' },
  { key: 'embedding', label: 'Embedding' },
  { key: 'waiting_for_cohort', label: 'Cohort wait' },
  { key: 'aeo_generation', label: 'AEO Gen' },
  { key: 'aeo_validation', label: 'AEO Validate' },
];

function progressOf(sb: SubBatch): number {
  switch (sb.currentPhase) {
    case 'enrichment': return sb.enrichment.succeeded || 0;
    case 'benefit_scoring': return sb.benefitScoring.succeeded || 0;
    case 'embedding': return sb.embedding.succeeded || 0;
    case 'aeo_generation': return sb.aeoGeneration.generated || 0;
    case 'aeo_validation': return sb.aeoValidation.validated || 0;
    default: return 0;
  }
}

export function PipelineStageView({ batch, realtime, prevBatch, prevRealtime }: Props) {
  const [tab, setTab] = useState<'batch' | 'realtime'>('batch');
  const active = tab === 'batch' ? batch : realtime;
  const prev = tab === 'batch' ? prevBatch : prevRealtime;

  const grouped = useMemo(() => {
    const map: Record<string, SubBatch[]> = {};
    for (const phase of PHASES) map[phase.key] = [];
    for (const sb of active) {
      const list = map[sb.currentPhase];
      if (list) list.push(sb);
    }
    return map;
  }, [active]);

  // For each sub-batch, detect if its progress increased since previous snapshot.
  const progressedIds = useMemo(() => {
    const set = new Set<string>();
    if (!prev) return set;
    const prevById = new Map(prev.map(s => [s.id, s]));
    for (const sb of active) {
      const before = prevById.get(sb.id);
      if (before && progressOf(sb) > progressOf(before)) set.add(sb.id);
    }
    return set;
  }, [active, prev]);

  if (!active.length) {
    return (
      <div className="rounded-xl border bg-white shadow-sm p-6 text-center text-sm text-gray-500">
        Pipeline is idle — no active {tab} sub-batches.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-max">
        {(['batch', 'realtime'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-1 text-xs font-medium rounded-md capitalize ${tab === t ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
          >
            {t} ({t === 'batch' ? batch.length : realtime.length})
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {PHASES.map(phase => (
          <div key={phase.key} className="rounded-xl border bg-gray-50 p-2 min-h-[160px]">
            <div className="text-[10px] font-mono uppercase tracking-wider font-semibold text-gray-600 mb-2 px-1">
              {phase.label}{' '}
              <span className="text-gray-400">({grouped[phase.key]?.length || 0})</span>
            </div>
            <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto">
              {(grouped[phase.key] || []).slice(0, 8).map(sb => (
                <SubBatchBlock key={sb.id} sb={sb} progressed={progressedIds.has(sb.id)} />
              ))}
              {(grouped[phase.key]?.length || 0) > 8 && (
                <div className="text-[10px] text-gray-400 font-mono text-center">
                  +{(grouped[phase.key]?.length || 0) - 8} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
